import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listarCatalogoEntregables,
  crearCatalogoEntregable,
  actualizarCatalogoEntregable,
  listarCursosAsignados,
  actualizarCursosAsignados,
} from "../../../services/catalogoEntregablesService";
import { listarCursos } from "../../../services/cursosService";
import { showToast } from "../../../utils/showToast";

// Modal de asignacion: que cursos deben mostrar este entregable en la ficha
// del estudiante (tabla cursos_entregables). Mismo wrapper .management-modal
// que ya usan MatriculaFormModal/EspecialidadesFormModal.
function CursosAsignadosModal({
  open,
  entregable,
  cursos,
  loadingCursos,
  seleccionados,
  onToggleCurso,
  filtro,
  onFiltro,
  onClose,
  onGuardar,
  saving,
}) {
  if (!open) return null;

  const texto = filtro.trim().toLowerCase();
  const cursosFiltrados = texto
    ? cursos.filter(
        (c) =>
          c.nombre?.toLowerCase().includes(texto) ||
          c.codigo_curso?.toLowerCase().includes(texto)
      )
    : cursos;

  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">
              Cursos asignados{entregable ? `: ${entregable.nombre}` : ""}
            </h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          <div className="modal-body pt-0">
            <p className="text-muted small mb-3">
              Marca los cursos en los que este entregable debe aparecer en la ficha del estudiante.
            </p>
            <input
              type="text"
              className="form-control mb-3"
              placeholder="Buscar curso por nombre o codigo..."
              value={filtro}
              onChange={(e) => onFiltro(e.target.value)}
            />
            {loadingCursos ? (
              <div className="text-muted small py-3 text-center">Cargando cursos...</div>
            ) : (
              <div className="border rounded p-2" style={{ maxHeight: 320, overflowY: "auto" }}>
                {cursosFiltrados.length ? (
                  cursosFiltrados.map((curso) => (
                    <div className="form-check py-1" key={curso.id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`curso-entregable-${curso.id}`}
                        checked={seleccionados.has(curso.id)}
                        onChange={() => onToggleCurso(curso.id)}
                      />
                      <label className="form-check-label" htmlFor={`curso-entregable-${curso.id}`}>
                        {curso.nombre}{" "}
                        <span className="text-muted small">({curso.codigo_curso})</span>
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-muted small mb-0 text-center py-3">
                    No hay cursos que coincidan con la busqueda.
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer border-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn management-btn-primary"
              onClick={onGuardar}
              disabled={saving || loadingCursos}
            >
              {saving ? "Guardando..." : "Guardar asignacion"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal de edicion: nombre/tipo/descripcion no se pueden cambiar desde los
// switches de la tabla (esos solo tocan flags puntuales) — este modal cubre
// el caso "esto ya no es lo que era" (ej. Guia de estudio paso de ser un
// libro fisico a un PDF, entonces cambia de tipo=articulo a documento).
function EditarEntregableModal({ item, onClose, onGuardado }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        nombre: item.nombre || "",
        tipo: item.tipo || "articulo",
        descripcion: item.descripcion || "",
        requiere_talla: !!item.requiere_talla,
        requiere_devolucion: !!item.requiere_devolucion,
      });
    }
  }, [item]);

  if (!item || !form) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      showToast({ type: "warning", message: "El nombre es obligatorio." });
      return;
    }
    setSaving(true);
    try {
      const actualizado = await actualizarCatalogoEntregable(item.id, {
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        descripcion: form.descripcion.trim() || null,
        requiere_talla: form.tipo === "articulo" && form.requiere_talla,
        requiere_devolucion: form.tipo === "articulo" && form.requiere_devolucion,
      });
      onGuardado(actualizado);
      showToast({ type: "success", message: "Entregable actualizado." });
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar el entregable." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header border-0">
              <h5 className="modal-title">Editar entregable</h5>
              <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
            </div>
            <div className="modal-body pt-0">
              <div className="mb-3">
                <label className="form-label">Nombre <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  value={form.nombre}
                  onChange={(e) => set("nombre", e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Tipo</label>
                <select className="form-select" value={form.tipo} onChange={(e) => set("tipo", e.target.value)}>
                  <option value="articulo">Articulo (fisico)</option>
                  <option value="documento">Documento (PDF)</option>
                </select>
                {item.tipo === "articulo" && form.tipo === "documento" && (
                  <small className="text-muted d-block mt-1">
                    Si este articulo esta vinculado a Inventario, el stock ya registrado no se borra,
                    pero dejara de aparecer como candidato para vincular productos nuevos.
                  </small>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Descripcion</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.descripcion}
                  onChange={(e) => set("descripcion", e.target.value)}
                />
              </div>
              {form.tipo === "articulo" && (
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="entregable-editar-talla"
                      checked={form.requiere_talla}
                      onChange={(e) => set("requiere_talla", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="entregable-editar-talla">
                      Requiere talla
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="entregable-editar-devolucion"
                      checked={form.requiere_devolucion}
                      onChange={(e) => set("requiere_devolucion", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="entregable-editar-devolucion">
                      Es prestamo (se devuelve)
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="btn management-btn-primary" disabled={saving}>
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const TIPO_LABEL = { articulo: "Articulo", documento: "Documento" };

export default function ConfiguracionEntregables() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [tipoNuevo, setTipoNuevo] = useState("articulo");
  const [tallaNuevo, setTallaNuevo] = useState(false);
  const [devolucionNuevo, setDevolucionNuevo] = useState(false);
  const [creando, setCreando] = useState(false);

  const [cursos, setCursos] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(false);
  const [cursosCargados, setCursosCargados] = useState(false);
  const [entregableAEditar, setEntregableAEditar] = useState(null);
  const [modalEntregable, setModalEntregable] = useState(null);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [filtroCursos, setFiltroCursos] = useState("");
  const [savingCursos, setSavingCursos] = useState(false);

  const cargar = () => {
    setLoading(true);
    listarCatalogoEntregables()
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() =>
        showToast({ type: "danger", message: "No se pudo cargar el catalogo de entregables." })
      )
      .finally(() => setLoading(false));
  };

  useEffect(cargar, []);

  useEffect(() => {
    if (!document?.body) return undefined;
    if (modalEntregable || entregableAEditar) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [modalEntregable, entregableAEditar]);

  const toggleActivo = async (item) => {
    setSavingId(item.id);
    try {
      await actualizarCatalogoEntregable(item.id, { activo: item.activo ? 0 : 1 });
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, activo: i.activo ? 0 : 1 } : i))
      );
      showToast({ type: "success", message: item.activo ? "Entregable desactivado." : "Entregable activado." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar." });
    } finally {
      setSavingId(null);
    }
  };

  const toggleFlag = async (item, campo) => {
    setSavingId(item.id);
    try {
      await actualizarCatalogoEntregable(item.id, { [campo]: item[campo] ? 0 : 1 });
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, [campo]: i[campo] ? 0 : 1 } : i))
      );
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar." });
    } finally {
      setSavingId(null);
    }
  };

  const agregar = async (e) => {
    e.preventDefault();
    if (!nombreNuevo.trim()) {
      showToast({ type: "warning", message: "Ingresa un nombre para el entregable." });
      return;
    }
    setCreando(true);
    try {
      const creado = await crearCatalogoEntregable({
        nombre: nombreNuevo.trim(),
        tipo: tipoNuevo,
        requiere_talla: tipoNuevo === "articulo" && tallaNuevo,
        requiere_devolucion: tipoNuevo === "articulo" && devolucionNuevo,
      });
      setItems((prev) => [...prev, { ...creado, cursos_asignados: 0 }]);
      setNombreNuevo("");
      setTipoNuevo("articulo");
      setTallaNuevo(false);
      setDevolucionNuevo(false);
      setMostrarForm(false);
      showToast({ type: "success", message: "Entregable agregado al catalogo." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo agregar el entregable." });
    } finally {
      setCreando(false);
    }
  };

  const abrirCursosModal = async (item) => {
    setModalEntregable(item);
    setFiltroCursos("");
    setCursosLoading(true);
    try {
      if (!cursosCargados) {
        const todos = await listarCursos();
        setCursos(Array.isArray(todos) ? todos : []);
        setCursosCargados(true);
      }
      const asignados = await listarCursosAsignados(item.id);
      setSeleccionados(new Set((Array.isArray(asignados) ? asignados : []).map(Number)));
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo cargar la asignacion de cursos." });
      setModalEntregable(null);
    } finally {
      setCursosLoading(false);
    }
  };

  const toggleCurso = (cursoId) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(cursoId)) next.delete(cursoId);
      else next.add(cursoId);
      return next;
    });
  };

  const guardarCursos = async () => {
    if (!modalEntregable) return;
    setSavingCursos(true);
    try {
      const cursoIds = [...seleccionados];
      await actualizarCursosAsignados(modalEntregable.id, cursoIds);
      setItems((prev) =>
        prev.map((i) =>
          i.id === modalEntregable.id ? { ...i, cursos_asignados: cursoIds.length } : i
        )
      );
      showToast({ type: "success", message: "Cursos actualizados correctamente." });
      setModalEntregable(null);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo guardar la asignacion." });
    } finally {
      setSavingCursos(false);
    }
  };

  return (
    <section className="management-page management-theme-teal configuracion-wrapper">
      <div className="container-fluid">
        <div className="management-card management-header mb-4">
          <div className="d-flex align-items-center gap-2 mb-1">
            <Link to="/dashboard/configuracion" className="text-muted text-decoration-none small">
              <i className="bi bi-chevron-left me-1"></i>Configuracion
            </Link>
          </div>
          <div className="dashboard-eyebrow">Configuracion / Entregables</div>
          <h5 className="mb-1">Catalogo de entregables</h5>
          <p className="text-muted mb-0">
            Activa, desactiva o agrega los articulos y documentos que se entregan a los estudiantes
            (camisas, mochilas, diplomas, carnet), y en que cursos aplica cada uno.
          </p>
        </div>

        {loading ? (
          <div className="text-muted">Cargando...</div>
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Entregable</th>
                    <th style={{ width: 110 }} className="text-center">Tipo</th>
                    <th style={{ width: 90 }} className="text-center">Talla</th>
                    <th style={{ width: 100 }} className="text-center">Devolucion</th>
                    <th style={{ width: 160 }} className="text-center">Cursos</th>
                    <th style={{ width: 90 }} className="text-center">Activo</th>
                    <th style={{ width: 70 }} className="text-center">Editar</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className={item.activo ? "" : "text-muted"}>
                      <td>{item.nombre}</td>
                      <td className="text-center">
                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            item.tipo === "documento" ? "bg-warning-subtle text-warning" : "bg-info-subtle text-info"
                          }`}
                        >
                          {TIPO_LABEL[item.tipo] || item.tipo}
                        </span>
                      </td>
                      <td className="text-center">
                        {item.tipo === "articulo" ? (
                          <div className="form-check form-switch d-flex justify-content-center">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!item.requiere_talla}
                              disabled={savingId === item.id}
                              onChange={() => toggleFlag(item, "requiere_talla")}
                            />
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {item.tipo === "articulo" ? (
                          <div className="form-check form-switch d-flex justify-content-center">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!item.requiere_devolucion}
                              disabled={savingId === item.id}
                              onChange={() => toggleFlag(item, "requiere_devolucion")}
                            />
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => abrirCursosModal(item)}
                        >
                          <i className="bi bi-mortarboard me-1" />
                          {item.cursos_asignados || 0} curso{item.cursos_asignados === 1 ? "" : "s"}
                        </button>
                      </td>
                      <td className="text-center">
                        <div className="form-check form-switch d-flex justify-content-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!!item.activo}
                            disabled={savingId === item.id}
                            onChange={() => toggleActivo(item)}
                          />
                        </div>
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setEntregableAEditar(item)}
                          title="Editar entregable"
                        >
                          <i className="bi bi-pencil-square" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card-body border-top">
              {mostrarForm ? (
                <form className="row g-2 align-items-end" onSubmit={agregar}>
                  <div className="col-12 col-md-4">
                    <label className="form-label small mb-1">Nombre del entregable</label>
                    <input
                      className="form-control"
                      placeholder="Ej: Chaleco reflectivo"
                      value={nombreNuevo}
                      onChange={(e) => setNombreNuevo(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Tipo</label>
                    <select
                      className="form-select"
                      value={tipoNuevo}
                      onChange={(e) => setTipoNuevo(e.target.value)}
                    >
                      <option value="articulo">Articulo (fisico)</option>
                      <option value="documento">Documento (PDF)</option>
                    </select>
                  </div>
                  {tipoNuevo === "articulo" && (
                    <div className="col-auto d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="entregable-nuevo-talla"
                          checked={tallaNuevo}
                          onChange={(e) => setTallaNuevo(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="entregable-nuevo-talla">
                          Requiere talla
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="entregable-nuevo-devolucion"
                          checked={devolucionNuevo}
                          onChange={(e) => setDevolucionNuevo(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="entregable-nuevo-devolucion">
                          Es prestamo (se devuelve)
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="col-auto d-flex gap-2">
                    <button type="submit" className="btn management-btn-primary" disabled={creando}>
                      {creando ? "Agregando..." : "Agregar"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setMostrarForm(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <button type="button" className="btn management-btn-primary" onClick={() => setMostrarForm(true)}>
                  <i className="bi bi-plus-lg me-1" />
                  Agregar entregable
                </button>
              )}
            </div>
          </div>
        )}

        <div className="alert alert-light border small mt-3">
          <i className="bi bi-info-circle me-1" />
          Si es un articulo que se debe controlar por stock (ej. camisas, mochilas), vinculalo
          ademas en <Link to="/dashboard/inventario">Inventario &gt; Stock actual</Link> para llevar su kardex.
        </div>
      </div>

      <CursosAsignadosModal
        open={!!modalEntregable}
        entregable={modalEntregable}
        cursos={cursos}
        loadingCursos={cursosLoading}
        seleccionados={seleccionados}
        onToggleCurso={toggleCurso}
        filtro={filtroCursos}
        onFiltro={setFiltroCursos}
        onClose={() => setModalEntregable(null)}
        onGuardar={guardarCursos}
        saving={savingCursos}
      />
      <EditarEntregableModal
        item={entregableAEditar}
        onClose={() => setEntregableAEditar(null)}
        onGuardado={(actualizado) =>
          setItems((prev) =>
            prev.map((i) => (i.id === entregableAEditar.id ? { ...i, ...actualizado } : i))
          )
        }
      />
    </section>
  );
}
